import type { QueryClient } from "@tanstack/react-query";

/*
 * Saves that can't trip over each other:
 *  - inOrder: saves to the same thing (e.g. one item) are sent one at a time, in the order made,
 *    so a quick second change never races (or lands before) the first.
 *  - whileSaving: while any save to a query's data is in flight, live updates don't refetch it
 *    (that would put back values from before the later saves, so changes look lost); it's
 *    refetched once, when the last save finishes.
 */

const queues = new Map<string, Promise<unknown>>();

/** Run `fn` after every earlier save with the same key has finished (whether it worked or not). */
export function inOrder<T>(key: string, fn: () => Promise<T>): Promise<T> {
    const run = (queues.get(key) ?? Promise.resolve()).catch(() => {}).then(fn);
    const tail = run.catch(() => {});
    queues.set(key, tail);
    void tail.then(() => {
        if (queues.get(key) === tail) queues.delete(key);
    });
    return run;
}

const pending = new Map<string, number>();
const id = (queryKey: readonly unknown[]) => JSON.stringify(queryKey);

/** Is a save to this query's data (or a query under it) in flight? */
export function isSaving(queryKey: readonly unknown[]) {
    for (const [k, n] of pending) {
        if (n > 0 && id(queryKey).startsWith(k.slice(0, -1))) return true;
    }
    return false;
}

/**
 * Run a save to `queryKey`'s data; after the last one in flight, refetch it (and wait for that), or
 * `refresh` it some other way (e.g. just the records saved: the Price List's liveRows.ts).
 */
export async function whileSaving<T>(qc: QueryClient, queryKey: readonly unknown[], fn: () => Promise<T>, refresh = () => qc.invalidateQueries({ queryKey })): Promise<T> {
    const k = id(queryKey);
    pending.set(k, (pending.get(k) ?? 0) + 1);
    try {
        return await fn();
    } finally {
        const left = (pending.get(k) ?? 1) - 1;
        if (left > 0) pending.set(k, left);
        else {
            pending.delete(k);
            await refresh();
        }
    }
}
